import * as React from 'react';

import { translate } from '~l10n/client';

import { UIButton } from '~shared/components/buttons';
import { Dialog, DialogTitle } from '~shared/components/Dialog';
import { useAutoFocus } from '~shared/components/helpers';
import { Inline } from '~shared/components/Inline';
import { RadioGroup, RadioItem } from '~shared/components/RadioGroup';
import { Stack } from '~shared/components/Stack';
import TextInput from '~shared/components/TextInput';
import { ReactNodeNoStrings } from '~shared/types';

type QuizToMerge = { quizName: string; quizId: string };

interface Props {
  onClose: (success?: boolean) => void;
  isOpen: boolean;
  quizzesToMerge: Array<QuizToMerge>;
  onMergeQuiz: ({
    quizName,
    quizOrder,
  }: {
    quizName: string;
    quizOrder: string[];
  }) => Promise<void>;
  children?: ReactNodeNoStrings;
}

function useResetQuizDialog({
  isOpen,
  setMergedQuizName,
  setIsMergeDisabled,
  setSelectedQuizId,
}: {
  isOpen: boolean;
  setMergedQuizName: React.Dispatch<React.SetStateAction<string>>;
  setIsMergeDisabled: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedQuizId: React.Dispatch<string>;
}) {
  return React.useEffect(() => {
    if (isOpen) {
      setIsMergeDisabled(false);
      setMergedQuizName(translate('Merged Quiz'));
      setSelectedQuizId(null);
    }
  }, [isOpen, setIsMergeDisabled, setMergedQuizName, setSelectedQuizId]);
}

function useOnChangeSelectedQuiz(
  setSelectedQuizId: React.Dispatch<React.SetStateAction<string>>
) {
  return React.useCallback(
    (event: any) => {
      setSelectedQuizId(event.target.value);
    },
    [setSelectedQuizId]
  );
}

function useOnChangeMergedQuizName(
  setMergedQuizName: React.Dispatch<React.SetStateAction<string>>
) {
  return React.useCallback(
    (event) => setMergedQuizName(event.target.value),
    [setMergedQuizName]
  );
}

export function reorderQuizzesBasedOnSelected({
  quizzesToMerge,
  selectedQuizId,
}: {
  quizzesToMerge: Array<QuizToMerge>;
  selectedQuizId: string;
}) {
  return [
    selectedQuizId,
    ...quizzesToMerge
      .filter(({ quizId }) => quizId !== selectedQuizId)
      .map((quiz) => quiz.quizId),
  ];
}

export function MergeQuizDialog({
  onClose,
  isOpen,
  quizzesToMerge,
  onMergeQuiz,
  children,
}: Props) {
  const [mergedQuizName, setMergedQuizName] = React.useState('');
  const [selectedQuizId, setSelectedQuizId] = React.useState<string>(null);
  const [isMergeDisabled, setIsMergeDisabled] = React.useState(false);

  useResetQuizDialog({
    isOpen: isOpen,
    setMergedQuizName,
    setSelectedQuizId,
    setIsMergeDisabled,
  });

  const onChangeMergedQuizName = useOnChangeMergedQuizName(setMergedQuizName);

  const onChangeSelectedQuiz = useOnChangeSelectedQuiz(setSelectedQuizId);
  const autoFocusOnOpenCallback = useAutoFocus();

  const handleMergeQuiz = () => {
    setIsMergeDisabled(true);
    const quizOrder = reorderQuizzesBasedOnSelected({
      quizzesToMerge: quizzesToMerge,
      selectedQuizId: selectedQuizId,
    });
    onMergeQuiz({ quizName: mergedQuizName, quizOrder }).then(
      () => {
        onClose(true);
      },
      () => setIsMergeDisabled(false)
    );
  };

  return (
    <Dialog
      padding="confirmation"
      onClose={() => onClose(false)}
      id="merge-quiz-dialog"
      open={isOpen}
      title={translate('Merge Quizzes')}
      closeLabel={translate('Close')}
    >
      {({ headingRef, title }) => (
        <div className="text-left">
          <Stack space="4">
            <h3 className="m-0">
              <DialogTitle ref={headingRef}>{title}</DialogTitle>
            </h3>
            <div className="text-base leading-8">
              <label htmlFor="merged-quiz-name">
                {translate('Merged Quiz Name')}
              </label>
              <TextInput
                ref={autoFocusOnOpenCallback}
                maxLength={255}
                id="merged-quiz-name"
                value={mergedQuizName}
                onChange={onChangeMergedQuizName}
              />
            </div>
            <span className="text-base text-blue-900">
              {translate(
                'Which questions should come first in the merged quiz?'
              )}
            </span>
            <RadioGroup
              id="quiz-order"
              value={selectedQuizId}
              layout="stack"
              onChange={onChangeSelectedQuiz}
            >
              {quizzesToMerge.map(({ quizId, quizName }) => (
                <RadioItem
                  key={quizId}
                  id={quizId}
                  value={quizId}
                  label={quizName}
                >
                  {({ checked }) => (
                    <>
                      <input id="uniqueId" type="radio" checked={checked} />
                      <label
                        className="text-base text-blue-900"
                        htmlFor={quizId}
                      >
                        {quizName}
                      </label>
                    </>
                  )}
                </RadioItem>
              ))}
            </RadioGroup>
            {children}
          </Stack>
          <div className="pt-8">
            <Inline align="right">
              <UIButton onClick={() => onClose(false)}>
                {translate('Cancel')}
              </UIButton>
              <UIButton
                appearance="primary"
                isDisabled={isMergeDisabled || selectedQuizId === null}
                onClick={handleMergeQuiz}
              >
                {translate('Merge')}
              </UIButton>
            </Inline>
          </div>
        </div>
      )}
    </Dialog>
  );
}
